/**
 * How the link to whoever is driving is doing: the latency they feel and every stutter.
 *
 * The numbers are the arm's (status.perf, `pick_place.teleop.link_perf`): it times the
 * controller frames as they land and collects what the driver's headset measured of the video.
 * So this panel shows the operator's link wherever it is opened -- including on a laptop across
 * the room from the person in the headset -- and the same numbers go into every episode.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { agoText, controlsMs, estText, eventText, msText, spreadText } from "../adamo/perfText";
import type { Perf, PerfEvent, Status } from "../adamo/types";

const HISTORY_S = 120;
const SAMPLE_MS = 1000;

type Sample = { t: number; controls: number | null; video: number | null };
type Mark = { t: number; event: PerfEvent };

/** The arm sends the last ten seconds; the page keeps two minutes of them, one point a second. */
function useHistory(perf: Perf | undefined) {
  const latest = useRef(perf);
  latest.current = perf;
  const [samples, setSamples] = useState<Sample[]>([]);
  const [marks, setMarks] = useState<Mark[]>([]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const p = latest.current;
      const now = Date.now();
      const keep = (t: number) => now - t <= HISTORY_S * 1000;
      setSamples((old) => [...old.filter((s) => keep(s.t)),
        { t: now, controls: p ? controlsMs(p).ms : null, video: p?.video?.ms?.p50 ?? null }]);
      if (!p) return;
      setMarks((old) => {
        const fresh = p.events
          .filter((e) => e.kind !== "input_lost")
          .map((e) => ({ t: now - e.ago_s * 1000, event: e }))
          // the arm repeats its last eight in every status: the same stutter twice is one stutter
          .filter((m) => !old.some((o) => o.event.kind === m.event.kind && o.event.ms === m.event.ms
                                          && Math.abs(o.t - m.t) < 2000));
        return [...old.filter((m) => keep(m.t)), ...fresh];
      });
    }, SAMPLE_MS);
    return () => window.clearInterval(timer);
  }, []);
  return { samples, marks };
}

export function LinkPanel({ status, stale }: { status: Status | null; stale: boolean }) {
  const perf = status?.perf;
  const { samples, marks } = useHistory(stale ? undefined : perf);

  if (!status) return <p className="note">waiting for the robot…</p>;
  if (!perf) return <p className="note">This arm's program does not measure its link. It needs the one with link_perf.</p>;

  const c = perf.controls;
  const v = perf.video;
  const controls = controlsMs(perf);
  const felt = perf.felt_ms ?? (controls.ms !== null && v?.ms ? controls.ms + v.ms.p50 : null);
  const quietFor = c ? c.age_ms / 1000 : null;
  const sending = quietFor !== null && quietFor < 2;
  return (
    <>
      <p className="note" style={{ marginTop: -4 }}>
        {c ? <>to <b>{c.from}</b>{sending ? "" : `, whose last controller frame was ${agoText(quietFor!)}`}</> : "nobody has driven since the arm started"}
        {" · "}{clockText(perf)}
      </p>
      <div className="tiles">
        <Tile label="Controls → arm" value={estText(controls)}
              sub={!c ? "no frames" : controls.estimated
                ? `half the round trip · ${c.hz} Hz · ${c.lost_pct}% lost`
                : `p95 ${msText(c.ms?.p95)} · ${c.hz} Hz · ${c.lost_pct}% lost`} />
        <Tile label="Video → headset" value={msText(v?.ms?.p50)}
              sub={v ? `p95 ${msText(v.ms?.p95)} · ${v.fps ?? "—"} fps` : "the headset reports this while in VR"} />
        <Tile label="Felt" value={`${controls.estimated && felt !== null ? "~" : ""}${msText(felt)}`}
              sub="controls + video: a stick moved, to that seen" />
        <Tile label="Stutters, last minute" value={String(perf.stutters_1m)} tone={perf.stutters_1m ? "warn" : undefined}
              sub={perf.episode ? `this pick: ${perf.episode.stutters}` : "none recording"} />
      </div>

      <LatencyChart samples={samples} marks={marks} />

      <div className="rows" style={{ marginTop: 12 }}>
        <Row k="round trip" v={perf.rtt_ms ? `${spreadText(perf.rtt_ms)} · frame out, arm echoes it back` : "—"} />
        <Row k="longest gap in controls" v={msText(c?.max_gap_ms)} />
        <Row k="headset" v={perf.headset ? `${perf.headset.fps ?? "—"} fps · ${perf.headset.dropped} frames dropped` : "—"} />
        <Row k="video frames dropped" v={v ? String(v.dropped) : "—"} />
        <Row k="arm's control loop" v={perf.loop ? `${perf.loop.hz} Hz · p99 tick ${msText(perf.loop.p99_ms)}` : "—"} />
        {Object.entries(perf.cameras).map(([name, cam]) => (
          <Row key={name} k={`${name} camera`} v={`${cam.fps ?? "—"} fps · ${cam.skips_1m} skip${cam.skips_1m === 1 ? "" : "s"} this minute`} />
        ))}
      </div>

      <h3 className="minor">Recent stutters</h3>
      {perf.events.length === 0
        ? <p className="note">none</p>
        : <ul className="events">
            {perf.events.map((e, i) => (
              <li key={i} className={e.kind === "input_lost" ? "quiet" : ""}>
                <span>{eventText(e)}</span><span className="k">{agoText(e.ago_s)}</span>
              </li>
            ))}
          </ul>}

      {perf.last_episode && (
        <>
          <h3 className="minor">Last recorded episode</h3>
          <div className="rows">
            <Row k="length" v={`${perf.last_episode.duration_s.toFixed(1)} s`} />
            <Row k="controls" v={perf.last_episode.controls_ms ? spreadText(perf.last_episode.controls_ms)
              : perf.last_episode.rtt_ms ? `~${msText(perf.last_episode.rtt_ms.p50 / 2)} (half the round trip)` : "not measured"} />
            <Row k="video" v={spreadText(perf.last_episode.video_ms)} />
            <Row k="stutters" v={`${perf.last_episode.stutters}${stutterBreakdown(perf.last_episode.stutter_counts)}`} />
          </div>
        </>
      )}
    </>
  );
}

function clockText(perf: Perf): string {
  if (!perf.clock.arm) return "the arm has not synced the relay's clock: controls are estimated from the round trip";
  if (perf.clock.page === false) return "the headset has not synced the relay's clock, so no video latency";
  return perf.clock.page_error_ms !== null ? `clocks synced to ±${Math.max(1, Math.round(perf.clock.page_error_ms))} ms` : "clock synced";
}

function stutterBreakdown(counts: Partial<Record<PerfEvent["kind"], number>>): string {
  const names: Partial<Record<PerfEvent["kind"], string>> = {
    input_gap: "controls", video_freeze: "video", headset_hitch: "headset", loop_late: "arm", camera_gap: "camera" };
  const parts = Object.entries(counts).filter(([, n]) => n).map(([k, n]) => `${n} ${names[k as PerfEvent["kind"]] ?? k}`);
  return parts.length ? ` (${parts.join(", ")})` : "";
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "warn" }) {
  return (
    <div className="tile">
      <div className="k">{label}</div>
      <div className={`big${tone ? ` ${tone}` : ""}`}>{value}</div>
      <div className="k small">{sub}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return <div className="row"><span className="k">{k}</span><span className="v">{v}</span></div>;
}

// ---- the chart --------------------------------------------------------------

const W = 640, H = 170, PAD_L = 40, PAD_R = 12, PAD_T = 10, PAD_B = 22;
const SERIES = [
  { key: "controls" as const, name: "Controls → arm", color: "var(--series-1)" },
  { key: "video" as const, name: "Video → headset", color: "var(--series-2)" },
];

function niceMax(v: number): number {
  for (const step of [50, 100, 200, 250, 500, 1000, 2000, 5000]) if (v <= step) return step;
  return Math.ceil(v / 5000) * 5000;
}

function LatencyChart({ samples, marks }: { samples: Sample[]; marks: Mark[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const now = samples.length ? samples[samples.length - 1].t : Date.now();
  const t0 = now - HISTORY_S * 1000;
  const top = useMemo(() => niceMax(Math.max(50, ...samples.flatMap((s) => [s.controls ?? 0, s.video ?? 0]))), [samples]);
  const x = (t: number) => PAD_L + ((t - t0) / (HISTORY_S * 1000)) * (W - PAD_L - PAD_R);
  const y = (v: number) => PAD_T + (1 - v / top) * (H - PAD_T - PAD_B);
  const ticks = [0, top / 2, top];

  const path = (key: "controls" | "video") => {
    let d = "";
    let pen = false;
    for (const s of samples) {
      const v = s[key];
      if (v === null) { pen = false; continue; }
      d += `${pen ? "L" : "M"}${x(s.t).toFixed(1)},${y(v).toFixed(1)}`;
      pen = true;
    }
    return d;
  };

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - box.left) / box.width) * W;
    const t = t0 + ((px - PAD_L) / (W - PAD_L - PAD_R)) * HISTORY_S * 1000;
    let best: number | null = null;
    samples.forEach((s, i) => { if (best === null || Math.abs(s.t - t) < Math.abs(samples[best].t - t)) best = i; });
    setHover(best);
  };

  const h = hover !== null ? samples[hover] : null;
  const near = h ? marks.filter((m) => Math.abs(m.t - h.t) <= 1500) : [];
  const empty = !samples.some((s) => s.controls !== null || s.video !== null);
  return (
    <figure className="chart">
      <figcaption>
        <span className="k">Latency, last two minutes</span>
        <span className="legend">
          {SERIES.map((s) => <span key={s.key}><i style={{ background: s.color }} />{s.name}</span>)}
          <span><i className="rug" />stutter</span>
        </span>
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Controls and video latency over the last two minutes"
           onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={PAD_L} x2={W - PAD_R} y1={y(v)} y2={y(v)} className="grid" />
            <text x={PAD_L - 6} y={y(v)} className="tick" textAnchor="end" dominantBaseline="middle">{Math.round(v)}</text>
          </g>
        ))}
        <text x={PAD_L} y={H - 4} className="tick">2 min ago</text>
        <text x={W - PAD_R} y={H - 4} className="tick" textAnchor="end">now</text>
        {marks.filter((m) => m.t >= t0).map((m, i) => (
          <line key={i} x1={x(m.t)} x2={x(m.t)} y1={H - PAD_B - 10} y2={H - PAD_B} className="rug" />
        ))}
        {SERIES.map((s) => (
          <path key={s.key} d={path(s.key)} fill="none" stroke={s.color} strokeWidth={2}
                strokeLinejoin="round" strokeLinecap="round" />
        ))}
        {h && (
          <g>
            <line x1={x(h.t)} x2={x(h.t)} y1={PAD_T} y2={H - PAD_B} className="crosshair" />
            {SERIES.map((s) => h[s.key] !== null && (
              <circle key={s.key} cx={x(h.t)} cy={y(h[s.key]!)} r={4} fill={s.color} className="marker" />
            ))}
          </g>
        )}
        {empty && <text x={(W + PAD_L) / 2} y={H / 2} className="tick" textAnchor="middle">nothing measured yet</text>}
      </svg>
      {h && (
        <div className="tooltip" style={{ left: `${(x(h.t) / W) * 100}%`,
                                          transform: `translateX(${x(h.t) < W * 0.2 ? 0 : x(h.t) > W * 0.8 ? -100 : -50}%)` }}>
          <div className="k">{agoText((now - h.t) / 1000)}</div>
          {SERIES.map((s) => (
            <div key={s.key} className="line"><i style={{ background: s.color }} /><b>{msText(h[s.key])}</b> <span className="k">{s.name}</span></div>
          ))}
          {near.map((m, i) => <div key={i} className="k">{eventText(m.event)}</div>)}
        </div>
      )}
      <details>
        <summary className="k">as a table</summary>
        <table>
          <thead><tr><th>when</th><th>controls</th><th>video</th></tr></thead>
          <tbody>
            {samples.slice(-30).reverse().map((s) => (
              <tr key={s.t}><td>{agoText((now - s.t) / 1000)}</td><td>{msText(s.controls)}</td><td>{msText(s.video)}</td></tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}
